import React, { forwardRef } from 'react';
import { clsx } from 'clsx';

export type TableSize = 'sm' | 'md' | 'lg';
export type TableVariant = 'default' | 'striped' | 'bordered';

export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  size?: TableSize;
  variant?: TableVariant;
  stickyHeader?: boolean;
}

const sizeStyles: Record<TableSize, string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
};

export const Table = forwardRef<HTMLTableElement, TableProps>(
  (
    {
      className,
      size = 'md',
      variant = 'default',
      stickyHeader,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <div className="overflow-x-auto">
        <table
          ref={ref}
          className={clsx(
            'w-full border-collapse',
            sizeStyles[size],
            variant === 'bordered' && 'border border-line',
            className
          )}
          {...props}
        >
          {children}
        </table>
      </div>
    );
  }
);

Table.displayName = 'Table';

export interface TableHeadProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  sticky?: boolean;
}

export const TableHead = forwardRef<HTMLTableSectionElement, TableHeadProps>(
  ({ className, sticky, children, ...props }, ref) => {
    return (
      <thead
        ref={ref}
        className={clsx(
          'bg-surface-muted',
          sticky && 'sticky top-0 z-10',
          className
        )}
        {...props}
      >
        {children}
      </thead>
    );
  }
);

TableHead.displayName = 'TableHead';

export interface TableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  striped?: boolean;
}

export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, striped, children, ...props }, ref) => {
    return (
      <tbody
        ref={ref}
        className={clsx(
          'divide-y divide-neutral-200',
          striped && '[&>tr:nth-child(even)]:bg-surface-muted',
          className
        )}
        {...props}
      >
        {children}
      </tbody>
    );
  }
);

TableBody.displayName = 'TableBody';

export interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  selected?: boolean;
  hoverable?: boolean;
}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, selected, hoverable = true, children, ...props }, ref) => {
    return (
      <tr
        ref={ref}
        className={clsx(
          'transition-colors',
          hoverable && 'hover:bg-surface-subtle/50',
          selected && 'bg-coral-50',
          className
        )}
        {...props}
      >
        {children}
      </tr>
    );
  }
);

TableRow.displayName = 'TableRow';

export interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'center' | 'right';
  truncate?: boolean;
}

const alignStyles: Record<string, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, align = 'left', truncate, children, ...props }, ref) => {
    return (
      <td
        ref={ref}
        className={clsx(
          'px-4 py-3 text-text',
          alignStyles[align],
          truncate && 'truncate max-w-[200px]',
          className
        )}
        {...props}
      >
        {children}
      </td>
    );
  }
);

TableCell.displayName = 'TableCell';

export interface TableHeaderCellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  sorted?: 'asc' | 'desc' | false;
  onSort?: () => void;
}

export const TableHeaderCell = forwardRef<HTMLTableCellElement, TableHeaderCellProps>(
  (
    {
      className,
      align = 'left',
      sortable,
      sorted,
      onSort,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <th
        ref={ref}
        className={clsx(
          'px-4 py-3 font-semibold text-muted bg-surface-muted',
          alignStyles[align],
          sortable && 'cursor-pointer select-none hover:bg-surface-subtle',
          className
        )}
        onClick={sortable ? onSort : undefined}
        {...props}
      >
        <div className={clsx('flex items-center gap-2', align === 'right' && 'justify-end', align === 'center' && 'justify-center')}>
          {children}
          {sortable && (
            <span className="inline-flex flex-col">
              <svg
                className={clsx(
                  'w-3 h-3 -mb-1',
                  sorted === 'asc' ? 'text-coral-500' : 'text-subtle'
                )}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M5 10l5-5 5 5H5z" />
              </svg>
              <svg
                className={clsx(
                  'w-3 h-3',
                  sorted === 'desc' ? 'text-coral-500' : 'text-subtle'
                )}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M5 10l5 5 5-5H5z" />
              </svg>
            </span>
          )}
        </div>
      </th>
    );
  }
);

TableHeaderCell.displayName = 'TableHeaderCell';

export interface TableFooterProps extends React.HTMLAttributes<HTMLTableSectionElement> {}

export const TableFooter = forwardRef<HTMLTableSectionElement, TableFooterProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <tfoot
        ref={ref}
        className={clsx('bg-surface-muted border-t border-line', className)}
        {...props}
      >
        {children}
      </tfoot>
    );
  }
);

TableFooter.displayName = 'TableFooter';
